import { createContext, useContext, ReactNode, useState, useCallback, useEffect } from 'react';
import { ForgeChallenge, SideQuest } from '../types';
import { forgeChallenge as initialForgeChallenge, sideQuests as initialSideQuests } from '../data/challenges';
import { useS3State } from '../hooks/useS3State';
import { usePolling } from '../hooks/usePolling';
import { s3Service } from '../services/s3Service';

interface ChallengeContextValue {
  forgeChallenge: ForgeChallenge;
  sideQuests: SideQuest[];
  toggleChallengeStatus: (id: string) => Promise<void>;
  isLoading: boolean;
  error: Error | null;
}

export const ChallengeContext = createContext<ChallengeContextValue | undefined>(undefined);

interface ChallengeProviderProps {
  children: ReactNode;
}

export function ChallengeProvider({ children }: ChallengeProviderProps) {
  // Initialize challenge completion status from S3
  const initialChallengeStatus = {
    [initialForgeChallenge.id]: false,
    ...initialSideQuests.reduce((acc, quest) => {
      acc[quest.id] = false;
      return acc;
    }, {} as Record<string, boolean>),
  };

  const [challengeStatus, setChallengeStatus, isLoading, error] = useS3State(
    'challenges',
    initialChallengeStatus
  );

  // Local state for challenges with updated completion status
  const [forgeChallenge, setForgeChallenge] = useState<ForgeChallenge>({
    ...initialForgeChallenge,
    isCompleted: challengeStatus[initialForgeChallenge.id] || false,
  });

  const [sideQuests, setSideQuests] = useState<SideQuest[]>(
    initialSideQuests.map((quest) => ({
      ...quest,
      isCompleted: challengeStatus[quest.id] || false,
    }))
  );

  // Update local challenge state when S3 state changes
  useEffect(() => {
    setForgeChallenge({
      ...initialForgeChallenge,
      isCompleted: challengeStatus[initialForgeChallenge.id] || false,
    });

    setSideQuests(
      initialSideQuests.map((quest) => ({
        ...quest,
        isCompleted: challengeStatus[quest.id] || false,
      }))
    );
  }, [challengeStatus]);

  // Toggle challenge completion status
  const toggleChallengeStatus = useCallback(
    async (id: string) => {
      const newStatus = {
        ...challengeStatus,
        [id]: !challengeStatus[id],
      };
      await setChallengeStatus(newStatus);
    },
    [challengeStatus, setChallengeStatus]
  );

  // Poll S3 every 60 seconds for updates from other devices
  const pollS3 = useCallback(async () => {
    try {
      const state = await s3Service.getState();
      // Only update if the state has changed
      if (JSON.stringify(state.challenges) !== JSON.stringify(challengeStatus)) {
        setChallengeStatus(state.challenges);
      }
    } catch (err) {
      console.error('Failed to poll S3 for challenge updates:', err);
    }
  }, [challengeStatus, setChallengeStatus]);

  usePolling(pollS3, 60000, true); // Poll every 60 seconds

  const value: ChallengeContextValue = {
    forgeChallenge,
    sideQuests,
    toggleChallengeStatus,
    isLoading,
    error,
  };

  return (
    <ChallengeContext.Provider value={value}>
      {children}
    </ChallengeContext.Provider>
  );
}

export function useChallenge() {
  const context = useContext(ChallengeContext);
  if (context === undefined) {
    throw new Error('useChallenge must be used within a ChallengeProvider');
  }
  return context;
}
